import { useState, useEffect, useCallback } from 'react';
import { Message } from '../types';
import { useWebgate } from '@xvia/webgate-connect';

const STORAGE_KEY = 'shared_chat_messages';
const VERSION_KEY = 'shared_chat_version';
const TIMESTAMP_KEY = 'shared_chat_timestamp';
const SYNC_EVENT = 'chat-sync';
const VERSION_EVENT = 'chat-version';
const DEFAULT_LIA_MESSAGE = "Olá, eu sou a Lia, sua assistente virtual e estou aqui para facilitar o seu acesso às informações e serviços públicos de maneira mais rápida e eficiente.";

const isBrowser = typeof window !== 'undefined';

const formatTime = (): string => {
  return new Date().toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const createMessage = (text: string, from: 'user' | 'lia', type: 'text' | 'audio' = 'text'): Message => ({
  from,
  text,
  time: formatTime(),
  type,
});

const getCurrentTimestamp = (): number => {
  return Date.now();
};

const isPageReloaded = (): boolean => {
  if (!isBrowser) return false;
  const navigationEntry = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming;
  return navigationEntry?.type === 'reload';
};

const clearChatMessages = (): void => {
  if (!isBrowser) return;
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(VERSION_KEY);
    localStorage.removeItem(TIMESTAMP_KEY);
  } catch (error) {
    console.error('Error clearing chat messages:', error);
  }
};

const getStoredMessages = (): { messages: Message[], version: number, timestamp: number } => {
  if (!isBrowser) return { messages: [], version: 0, timestamp: 0 };
  
  try {
    const storedMessages = localStorage.getItem(STORAGE_KEY);
    const storedVersion = localStorage.getItem(VERSION_KEY);
    const storedTimestamp = localStorage.getItem(TIMESTAMP_KEY);
    
    return {
      messages: storedMessages ? JSON.parse(storedMessages) : [],
      version: storedVersion ? parseInt(storedVersion, 10) : 0,
      timestamp: storedTimestamp ? parseInt(storedTimestamp, 10) : 0
    };
  } catch (error) {
    console.error('Error accessing localStorage:', error);
    return { messages: [], version: 0, timestamp: 0 };
  }
};

const saveToLocalStorage = (messages: Message[], version: number, timestamp: number): void => {
  if (!isBrowser) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    localStorage.setItem(VERSION_KEY, version.toString());
    localStorage.setItem(TIMESTAMP_KEY, timestamp.toString());
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

const dispatchSyncEvent = (messages: Message[], version: number, timestamp: number): void => {
  if (!isBrowser) return;
  try {
    window.dispatchEvent(new CustomEvent(SYNC_EVENT, { 
      detail: { messages, version, timestamp } 
    }));
  } catch (error) {
    console.error('Error dispatching sync event:', error);
  }
};

export const useSharedChat = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [messages, setMessagesState] = useState<Message[]>([]);
  const [version, setVersion] = useState(0);
  const [timestamp, setTimestamp] = useState(0);
  const [input, setInput] = useState('');
  const [isClient, setIsClient] = useState(false);
  const [_, setUserData] = useState<{ name: string } | null>(null);

  const { userInfo } = useWebgate();

  useEffect(() => {
    let message = DEFAULT_LIA_MESSAGE;

    if (userInfo) {
      setUserData(userInfo);

      message = `Olá, ${userInfo.name}, eu sou a Lia, sua assistente virtual e estou aqui para facilitar o seu acesso às informações e serviços públicos de maneira mais rápida e eficiente.`;
    } else {
      setUserData(null);
      clearChatMessages();
      setMessagesState([]);
      setVersion(1);
      setTimestamp(getCurrentTimestamp());
    }

    const from = 'lia';

    let firstMessage =  createMessage(
        message,
        from,
    );

    setMessagesState(prev => {
      const updatedMessages = [...prev];
      updatedMessages[0] = firstMessage;
      return updatedMessages;
    });
  }, [userInfo]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const setMessages = useCallback((updater: Message[] | ((prev: Message[]) => Message[])) => {
    setMessagesState(prev => {
      const newMessages = typeof updater === 'function' ? updater(prev) : updater;
      const newVersion = version + 1;
      const newTimestamp = getCurrentTimestamp();

      if (isClient) {
        saveToLocalStorage(newMessages, newVersion, newTimestamp);
        dispatchSyncEvent(newMessages, newVersion, newTimestamp);
      }

      setVersion(newVersion);
      setTimestamp(newTimestamp);
      return newMessages;
    });
  }, [version, isClient]);

  useEffect(() => {
    if (!isClient) return;

    try {
      const { messages: storedMessages, version: storedVersion, timestamp: storedTimestamp } = getStoredMessages();
      const currentTimestamp = getCurrentTimestamp();
      
      if (isPageReloaded() || currentTimestamp - storedTimestamp > 24 * 60 * 60 * 1000) {
        clearChatMessages();
        const initialMessage = createMessage(
          DEFAULT_LIA_MESSAGE,
          'lia'
        );
        setMessages([initialMessage]);
        setVersion(1);
        setTimestamp(currentTimestamp);
      } else {
        setMessages(storedMessages.length > 0 ? storedMessages : [createMessage(
          DEFAULT_LIA_MESSAGE,
          'lia'
        )]);
        setVersion(storedVersion);
        setTimestamp(storedTimestamp);
      }
    } catch (error) {
      console.error('Error initializing chat:', error);
      const initialMessage = createMessage(
        DEFAULT_LIA_MESSAGE,
        'lia'
      );
      setMessages([initialMessage]);
      setVersion(1);
      setTimestamp(getCurrentTimestamp());
    } finally {
      setIsLoading(false);
    }
  }, [isClient]);

  useEffect(() => {
    if (!isClient || isLoading) return;

    const handleSync = (event: CustomEvent<{ messages: Message[], version: number, timestamp: number }>) => {
      const { messages: newMessages, version: newVersion, timestamp: newTimestamp } = event.detail;
      
      if (newVersion > version || (newVersion === version && newTimestamp > timestamp)) {
        setMessagesState(newMessages);
        setVersion(newVersion);
        setTimestamp(newTimestamp);
      }
    };

    const handleVersion = (event: CustomEvent<{ version: number, timestamp: number }>) => {
      const { version: newVersion, timestamp: newTimestamp } = event.detail;
      if (newVersion > version || (newVersion === version && newTimestamp > timestamp)) {
        try {
          const { messages: storedMessages } = getStoredMessages();
          setMessagesState(storedMessages);
          setVersion(newVersion);
          setTimestamp(newTimestamp);
        } catch (error) {
          console.error('Error handling version update:', error);
        }
      }
    };

    window.addEventListener(SYNC_EVENT, handleSync as EventListener);
    window.addEventListener(VERSION_EVENT, handleVersion as EventListener);
    
    return () => {
      window.removeEventListener(SYNC_EVENT, handleSync as EventListener);
      window.removeEventListener(VERSION_EVENT, handleVersion as EventListener);
    };
  }, [isClient, isLoading, version, timestamp]);

  const clearMessages = useCallback(() => {
    if (!isClient) return;
    
    try {
      clearChatMessages();
      const newVersion = version + 1;
      const newTimestamp = getCurrentTimestamp();
      setVersion(newVersion);
      setTimestamp(newTimestamp);
      setMessages([createMessage(
        DEFAULT_LIA_MESSAGE,
        'lia'
      )]);
      
      if (isClient) {
        window.dispatchEvent(new CustomEvent(VERSION_EVENT, { 
          detail: { version: newVersion, timestamp: newTimestamp } 
        }));
      }
    } catch (error) {
      console.error('Error clearing messages:', error);
    }
  }, [version, isClient]);

  return {
    messages,
    setMessages,
    input,
    setInput,
    isLoading,
    clearMessages
  };
};
